import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from "@angular/core";
import { Location } from "@angular/common";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { BehaviorSubject, combineLatest, Subject, Subscription } from "rxjs";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { AnalysisAccount } from "../analysis/models/account.model";
import { AuthService } from "../auth/auth.service";
import { StudioUserSignupService } from "../auth/studio-user-signup.service";
import { debounceTime, map, tap } from "rxjs/operators";
import { UserStatus } from "../analysis/models/user.model";
import { LINKS } from "../shared/constants/links";

@Component({
    selector: "tdms-create-account",
    templateUrl: "./create-account.component.html",
    styleUrls: ["./create-account.component.scss"],
    standalone: false
})
export class CreateAccountComponent implements OnInit, OnDestroy {
  readonly LINKS = LINKS;
  createAccountForm!: UntypedFormGroup;
  isAccountCreated = false;
  criteria: boolean[] = [false, false, false, false, false];
  currentEmailInput = "";

  isAccountExistsPlatformOnly = false;
  displayExistingAcctModal = false;
  showTrailBlock = false;

  identityCheckDone = false;
  hasSubscriptionCheckDone = false;
  hasSubscription = false;

  isEmailCheckLoading = false;
  isAccessCheckLoading = false;

  institutionList: AnalysisAccount[] = [];
  emailSubscription!: Subscription;
  authSubscription!: Subscription;
  dialogSubscription!: Subscription;
  charCountChanged: Subject<number> = new Subject<number>();
  charCountLive: string = this.getCharMessage(0);


  @ViewChild("projectDescription", { static: false }) set rowCount(projectDescription: ElementRef){
    if (projectDescription) {
      this.setRowCount(projectDescription);
      projectDescription.nativeElement.addEventListener("input", () => {
        this.setRowCount(projectDescription);
      });
    }
  };

  private EXISTS = "exists";
  private DON_NOT_EXISTS = "dne";
  private UNVERIFIED = "unverified";
  private VERIFIED = "verified";

  errMsg: any;

  private emailSubjectSubscription!: Subscription;
  private emailSubject = new Subject<string>();

  private emailValidators = [
    Validators.pattern(/^\w+([\.\+\-]?\w+)*\@\w+([\.-]?\w+)*(\.\w{2,3})+$/),
    Validators.required,
  ];

  platformAccountExists$ = new BehaviorSubject<boolean>(false);

  constructor(
    private formBuilder: UntypedFormBuilder,
    private studioUserSignupService: StudioUserSignupService,
    private dialog: MatDialog,
    private route: Router,
    private location: Location,
    private titleService: Title
  ) {

    this.charCountChanged.pipe(
        debounceTime(1000), // Wait 500ms after the last input event
      ).subscribe(value => {
        console.log("Character count changed: ", value);
        this.charCountLive = this.getCharMessage(value);
      });
  }

  ngOnInit() {
    if (this.isAccountCreated) {
      this.titleService.setTitle("Verify Your Email");
    } else {
      this.titleService.setTitle("Create Account");
    }

    this.emailSubjectSubscription = this.emailSubject
      .pipe(debounceTime(300))
      .subscribe((emailInputValue) => {
        this.checkEmail(emailInputValue);
      });

    this.createAccountForm = this.formBuilder.group({
      email: ["", this.emailValidators],
      institution: ["", Validators.required],
      projectDescription: ["", Validators.maxLength(500)],
      acceptTerms: [false, Validators.requiredTrue],
    });

    this.createAccountForm.get("acceptTerms")!.valueChanges.subscribe((val) => {
      console.log("AcceptTerm valueChange ===>", val);
    });
  }

  onSubmit(userData: any) {
    if (this.createAccountForm.valid) {
      console.log(
        "Form is valid ...B4 submitting...",
        JSON.stringify(this.createAccountForm.value, null, 2)
      );
      this.createAccount(userData);
    }
  }

  onSubmitMr(userData: any) {
    if (this.createAccountForm.valid) {
      console.log(
        "Form is valid ...B4 submitting...MR createAccount",
        JSON.stringify(this.createAccountForm.value, null, 2)
      );
      this.createAccount(userData);
    }
  }

  onKeyup(emailInput: string, event: KeyboardEvent) {
    if (event.key === "Tab" || event.key === "Shift") {
      console.log("Tab or Shift keys are pressed...");
    } else {
      this.emailSubject.next(emailInput);
    }
  }

  checkEmail(email: string) {
    this.clearFlags();

    if (this.currentEmailInput === email) return;
    this.currentEmailInput = email;

    // var emailRegex = /^\w+([\.\+\-]?\w+)*\@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    const emailCtrl = this.createAccountForm.get("email");
    if (emailCtrl?.valid) {
      this.isEmailCheckLoading = true;

      const studioUserSignupStatus$ =
        this.studioUserSignupService.getUserStatus(email);
      const studioUserStatus$ =
        this.studioUserSignupService.getStudioUserStatus(email);

      const userStatus$ = combineLatest([
        studioUserSignupStatus$,
        studioUserStatus$,
      ]).pipe(
        tap(([signupStatus, studioUserStatus]) => {
          console.log(JSON.stringify(signupStatus, null, 2));
          console.log(JSON.stringify(studioUserStatus, null, 2));
        }),
        map(([signupStatus, studioUserStatus]) => {
          const combinedStatus: UserStatus = {
            ...signupStatus,
            studio_status: studioUserStatus.studio_status,
          };
          return combinedStatus;
        })
      );

      //MAIN:1 Identity Check
      userStatus$.subscribe(
        (identity) => {
          console.log(JSON.stringify(identity, null, 2));
          //const status = identity.status;

          if (identity.signup_status === this.UNVERIFIED) {
            //Step 1: Check to see if the user is signed up, but not verified their email.
            this.identityCheckDone = true;

            this.route.navigateByUrl("/unverified/" + email);
          } else if (
            identity.methods_status === this.EXISTS ||
            identity.studio_status === this.EXISTS ||
            identity.signup_status === this.VERIFIED
          ) {
            //Step 2: Display existing modal when either of the account exists.
            //Display existing account dialog
            this.displayExistingAcctModal = true;
            this.identityCheckDone = true;

            if (this.dialog.openDialogs.length === 0) {
              const dialogRef = this.dialog.open(ExistingAccountModal, {
                disableClose: false,
                width: "685px",
                maxWidth: "850px",
              });
              this.dialogSubscription = dialogRef
                .afterClosed()
                .subscribe((result) => {
                  this.criteria = [false, false, false, false];
                  this.clearForm();
                  this.clearFlags();
                });
            }
          } else if (identity.platform_status === this.EXISTS) {
            //Step 3: Platform account exists (either myResearch or access to another application.)
            //Let the user create the account without asking for password.
            this.isAccountExistsPlatformOnly = true;
            this.identityCheckDone = true;
            //Inform the platform observable about account existence.
            this.informPlatformAccountSubject(true);
            this.accountAccessCheck(email);
          } else {
            //Step 4: This means -- It is a brand new account.
            //Check to see if the user's instituation (based on email domain) subscribed to TDM.

            //If subscribed  --Create account.
            //Else Display --- Trial block.
            this.informPlatformAccountSubject(false);
            this.accountAccessCheck(email);
            this.identityCheckDone = true;
          }
        },
        (catchError) => {
          console.log(
            "Error occurred whilte checking the user status ",
            catchError
          );
          this.isEmailCheckLoading = false;
        },
        () => {
          console.log("Email-Check: User status completed...");
          this.isEmailCheckLoading = false;
        }
      );
    }
  }

  informPlatformAccountSubject(exists: boolean) {
    this.platformAccountExists$.next(exists);
  }

  accountAccessCheck(email: string) {
    this.emailSubscription = this.studioUserSignupService
      .getUserAccounts(email)
      .subscribe(
        (accountList) => {
          if ((accountList.authAccountCount ?? 0) > 0) {
            this.institutionList = accountList.authAccount ?? [];
            this.checkAccess(this.institutionList[0].accountId);
            this.createAccountForm.patchValue({
              institution: this.institutionList[0].accountId,
            });
          } else {
            this.hasSubscription = false;
          }
        },
        (catchError) => {
          this.isEmailCheckLoading = false;
          console.log("Create Account Get Accounts Error: ", catchError);
        },
        () => {
          this.hasSubscriptionCheckDone = true;
          this.isEmailCheckLoading = false;
        }
      );
  }

  clearFlags() {
    this.isAccountExistsPlatformOnly = false;
    this.displayExistingAcctModal = false;
    this.identityCheckDone = false;
    this.institutionList = [];
    this.hasSubscription = false;
    this.hasSubscriptionCheckDone = false;
    this.errMsg = null;
    this.currentEmailInput = "";
  }

  checkAccess(institution: any) {
    this.errMsg = null;
    this.isAccessCheckLoading = true;
    this.studioUserSignupService.getAccount(institution).subscribe(
      (acctDetails) => {
        if (
          !acctDetails.hasMethodsAccess &&
          !acctDetails.hasWorkbenchAccess &&
          !acctDetails.hasUnlimitedWorkbenchAccess
        ) {
          this.hasSubscription = false;
        } else {
          this.hasSubscription = true;
        }
      },
      (error) => {
        this.isAccessCheckLoading = false;
        this.errMsg = error;
        this.hasSubscriptionCheckDone = false;
      },
      () => {
        this.identityCheckDone = true;
        this.hasSubscriptionCheckDone = true;
        this.isAccessCheckLoading = false;
      }
    );
  }

  createAccount(userData: any) {
    if (userData.institution) {
      this.emailSubscription = this.studioUserSignupService
        .getAccount(userData.institution)
        .subscribe(
          (acctDetails) => {
            if (
              acctDetails.hasMethodsAccess ||
              acctDetails.hasWorkbenchAccess ||
              acctDetails.hasUnlimitedWorkbenchAccess
            ) {
              this.authSubscription = this.studioUserSignupService
                .createAccount(userData, acctDetails)
                .subscribe(
                  (response) => {
                    this.isAccountCreated = true;
                    this.titleService.setTitle("Verify Your Email");
                  },
                  (catchError) => {
                    console.log("Account Creation Error: ", catchError);
                    this.isAccountExistsPlatformOnly = false;
                  }
                );
            }
          },
          (catchError) => {
            console.log("Account Creation Error: ", catchError);
            this.isAccountExistsPlatformOnly = false;
          }
        );
    }
  }

  clearForm() {
    this.createAccountForm = this.formBuilder.group({
      email: ["", this.emailValidators],
      institution: ["", Validators.required],
      acceptTerms: [false, Validators.requiredTrue]
    });

    this.institutionList = [];
  }

  ngOnDestroy() {
    if (this.emailSubscription) {
      this.emailSubscription.unsubscribe();
    }
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
    if (this.dialogSubscription) {
      this.dialogSubscription.unsubscribe();
    }
    if (this.emailSubjectSubscription) {
      this.emailSubjectSubscription.unsubscribe();
    }
  }

  getCharMessage(projectDescriptionLength: number): string {
    const maxLength = 150;
    if (projectDescriptionLength > maxLength) {
      let characterLimit = (projectDescriptionLength - maxLength) == 1 ? " character over the limit" : " characters over the limit";
      return (projectDescriptionLength - maxLength) + characterLimit;
    } else {
      let characterLimit = (maxLength - projectDescriptionLength) == 1 ? " character left" : " characters left";
      return (maxLength - projectDescriptionLength) + characterLimit;
    }
  }

  setRowCount(projectDescription: ElementRef) {
    const height = projectDescription.nativeElement.scrollHeight;
    const lineHeight = projectDescription.nativeElement.rows > 1 ? 24 : 38;
    projectDescription.nativeElement.rows = Math.min(Math.max(Math.floor(height / lineHeight), 1), 10);
    projectDescription.nativeElement.style.lineHeight = projectDescription.nativeElement.rows > 1 ? "24px" : "38px";
  }

  get acceptTerms() {
    return this.createAccountForm.get("acceptTerms")!.value;
  }

  get emailError(): boolean {
    return (
      this.createAccountForm.get("email")!.invalid &&
      this.createAccountForm.get("email")!.touched
    );
  }

  get displayRequestTrail(): boolean {
    return (
      this.identityCheckDone &&
      this.hasSubscriptionCheckDone &&
      !this.hasSubscription &&
      !this.isAccessCheckLoading
    );
  }
}

@Component({
    selector: "existing-account-modal",
    templateUrl: "existing-account-modal.html",
    styleUrls: ["./create-account.component.scss"],
    standalone: false
})
export class ExistingAccountModal {
  constructor(
    public dialogRef: MatDialogRef<ExistingAccountModal>,
    private auth: AuthService
  ) {}

  onLogin() {
    this.auth.login("/workbenchdashboard");
    this.dialogRef.close();
  }
}

@Component({
    selector: "no-access-modal",
    templateUrl: "no-access-modal.html",
    styleUrls: ["./create-account.component.scss"],
    standalone: false
})
export class NoAccessModal {
  constructor(public dialogRef: MatDialogRef<NoAccessModal>) {}
}

@Component({
    selector: "submission-modal",
    templateUrl: "submission-modal.html",
    styleUrls: ["./create-account.component.scss"],
    standalone: false
})
export class SubmissionModal {
  constructor(public dialogRef: MatDialogRef<NoAccessModal>) {}
}
